import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { TIDS } from "@/constants/testIds";
import { Camera, MicOff, Video, VideoOff, Mic, Radio, X, Loader2, Monitor } from "lucide-react";
import { useStreaming } from "@/lib/streaming";
import PipOverlayControls from "@/components/PipOverlayControls";

// ICE configuration — STUN reaches ~85% of connections; TURN (via env) covers the rest.
// Set REACT_APP_TURN_URL / REACT_APP_TURN_USERNAME / REACT_APP_TURN_CREDENTIAL
// (from Twilio, Metered, Xirsys, or a self-hosted coturn) to unlock the last mile.
const ICE_SERVERS = (() => {
  const base = [
    { urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302", "stun:stun2.l.google.com:19302"] },
  ];
  const turnUrl = process.env.REACT_APP_TURN_URL;
  if (turnUrl) {
    base.push({
      urls: turnUrl.split(",").map(s => s.trim()),
      username: process.env.REACT_APP_TURN_USERNAME || "",
      credential: process.env.REACT_APP_TURN_CREDENTIAL || "",
    });
  }
  return base;
})();

export default function StreamPlayer({
  isStreamer,
  channel,
  ws,
  peerId,
  onGoLive,
  onOffline,
}) {
  const streaming = useStreaming();
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const viewerPCRef = useRef(null); // viewer side
  const streamerPeerRef = useRef(null);
  // Streamer live state comes from the global provider so the stream survives navigation.
  const liveLocal = isStreamer
    ? (streaming.isLive && streaming.channelUsername === channel?.username)
    : false;
  const camOn = isStreamer ? streaming.camOn : true;
  const micOn = isStreamer ? streaming.micOn : true;
  const screenShare = isStreamer ? streaming.screenShare : false;
  const [hasRemote, setHasRemote] = useState(false);
  // 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed'
  const [connState, setConnState] = useState("idle");

  // Attach diagnostic ICE/connection state listeners to a PeerConnection.
  const wireDiagnostics = (pc, label) => {
    pc.oniceconnectionstatechange = () => {
      const s = pc.iceConnectionState;
      // eslint-disable-next-line no-console
      console.info(`[WebRTC:${label}] ICE →`, s);
      if (s === "checking" || s === "new") setConnState("connecting");
      else if (s === "connected" || s === "completed") setConnState("connected");
      else if (s === "disconnected") setConnState("reconnecting");
      else if (s === "failed" || s === "closed") setConnState("failed");
    };
    pc.onconnectionstatechange = () => {
      // eslint-disable-next-line no-console
      console.info(`[WebRTC:${label}] Connection →`, pc.connectionState);
    };
  };

  // ===== STREAMER SIDE =====
  const [pickerOpen, setPickerOpen] = useState(false);
  const [devices, setDevices] = useState({ cameras: [], mics: [] });
  const [selectedCam, setSelectedCam] = useState("");
  const [selectedMic, setSelectedMic] = useState("");
  const startingMedia = streaming.starting;

  // Attach the shared MediaStream to our local <video> whenever the provider
  // has one. The provider keeps the same stream across route changes.
  useEffect(() => {
    if (!isStreamer) return undefined;
    const el = localVideoRef.current;
    if (!el) return undefined;
    return streaming.attachPreview(el);
  }, [isStreamer, streaming, liveLocal]);

  const openPicker = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("This browser doesn't support live streaming.", { description: "Try Chrome, Edge, or Firefox on desktop." });
      return;
    }
    setPickerOpen(true);
    // Request a temporary permission so device labels are available
    try {
      const probe = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      probe.getTracks().forEach(t => t.stop());
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        cameras: list.filter(d => d.kind === "videoinput"),
        mics:    list.filter(d => d.kind === "audioinput"),
      });
    } catch (e) {
      // Permissions denied at probe — surface a clear reason but leave the picker open so the user can retry
      handleMediaError(e);
    }
  };

  const handleMediaError = (e) => {
    const name = e && e.name;
    // eslint-disable-next-line no-console
    console.error("[Go Live] getUserMedia failed:", name, e);
    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
      toast.error("Camera & mic permission denied", {
        description: "Click the camera icon in your browser's address bar to allow access, then try again.",
        duration: 6000,
      });
    } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
      toast.error("No camera or microphone found", { description: "Plug in a webcam or headset and try again." });
    } else if (name === "NotReadableError" || name === "TrackStartError") {
      toast.error("Camera is in use by another app", { description: "Close Zoom / Meet / OBS and try again." });
    } else if (name === "OverconstrainedError") {
      toast.error("Selected device won't accept these settings", { description: "Try a different camera or lower resolution." });
    } else {
      toast.error("Couldn't start your stream", { description: (e && e.message) || String(e) });
    }
  };

  const startMedia = async ({ cameraId, micId, screen = false, withCamera = false } = {}) => {
    try {
      await streaming.startStream(channel.username, { cameraId, micId, screen, withCamera });
      setPickerOpen(false);
      onGoLive && onGoLive();
    } catch (e) {
      handleMediaError(e);
    }
  };
  const stopMedia = () => {
    streaming.stopStream();
    onOffline && onOffline();
  };
  const toggleCam = () => streaming.toggleCam();
  const toggleMic = () => streaming.toggleMic();

  // Streamer WebRTC signaling is now handled globally inside StreamingProvider
  // (offer/answer/ice from viewers). Keeping it here would break stream
  // persistence across route changes.

  // Auto-open the camera picker when the streamer arrives with ?golive=1 so
  // clicking "Go Live" from the dashboard quick-action immediately starts
  // broadcasting (previously the flag flipped but no camera was opened).
  const [searchParams, setSearchParams] = useSearchParams();
  const autoTriggeredRef = useRef(false);
  useEffect(() => {
    if (!isStreamer || autoTriggeredRef.current) return;
    if (searchParams.get("golive") !== "1") return;
    if (liveLocal || streaming.starting) return;
    autoTriggeredRef.current = true;
    // Consume the param so a refresh doesn't retrigger the picker unexpectedly.
    const next = new URLSearchParams(searchParams);
    next.delete("golive");
    setSearchParams(next, { replace: true });
    // Small delay so the page has a chance to hydrate the streaming provider.
    setTimeout(() => { openPicker(); }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreamer, searchParams, liveLocal]);

  // ===== VIEWER SIDE =====
  useEffect(() => {
    if (!ws || isStreamer || !peerId) return;
    const handler = async (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === "streamer_live" || (msg.type === "presence" && msg.streamer_peer)) {
        const sp = msg.streamer_peer;
        if (!sp || sp === streamerPeerRef.current) return;
        streamerPeerRef.current = sp;
        await requestStream(sp);
      } else if (msg.type === "streamer_offline") {
        cleanupViewer();
      } else if (msg.type === "signal") {
        const data = msg.data;
        const pc = viewerPCRef.current;
        if (!pc) return;
        if (data?.type === "answer") {
          await pc.setRemoteDescription(new RTCSessionDescription(data));
        } else if (data?.type === "ice") {
          try { await pc.addIceCandidate(data.candidate); } catch { /* noop */ }
        }
      }
    };
    ws.addEventListener("message", handler);
    return () => ws.removeEventListener("message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, isStreamer, peerId]);

  const requestStream = async (streamerPeer) => {
    cleanupViewer();
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    viewerPCRef.current = pc;
    wireDiagnostics(pc, "viewer");
    setConnState("connecting");
    pc.addTransceiver("video", { direction: "recvonly" });
    pc.addTransceiver("audio", { direction: "recvonly" });
    pc.ontrack = (ev) => {
      if (remoteVideoRef.current && ev.streams[0]) {
        remoteVideoRef.current.srcObject = ev.streams[0];
        setHasRemote(true);
      }
    };
    pc.onicecandidate = (ev) => {
      if (ev.candidate) ws.send(JSON.stringify({ type: "signal", to: streamerPeer, data: { type: "ice", candidate: ev.candidate } }));
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    ws.send(JSON.stringify({ type: "signal", to: streamerPeer, data: { type: "offer", sdp: offer.sdp } }));
  };
  const cleanupViewer = () => {
    if (viewerPCRef.current) { viewerPCRef.current.close(); viewerPCRef.current = null; }
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    // Reset the tracked streamer peer so a fresh streamer_live event (e.g.
    // after the streamer refreshes their tab) triggers a full re-negotiation
    // rather than being deduped as "same peer".
    streamerPeerRef.current = null;
    setHasRemote(false);
    setConnState("idle");
  };

  useEffect(() => {
    // Cleanup on unmount — only the VIEWER side. The streamer side lives in
    // the global provider and MUST NOT be torn down on route change.
    return () => { cleanupViewer(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showVideo = isStreamer ? liveLocal : hasRemote;

  return (
    <div className="relative bg-black border border-white/10 rounded-md overflow-hidden aspect-video">
      {isStreamer ? (
        <video data-testid={TIDS.streamVideo} ref={localVideoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${liveLocal ? "block" : "hidden"}`} />
      ) : (
        <video data-testid={TIDS.streamVideo} ref={remoteVideoRef} autoPlay playsInline className={`w-full h-full object-cover ${hasRemote ? "block" : "hidden"}`} />
      )}

      {/* Streamer-only draggable/resizable Camera PiP editor (only visible in Screen + Camera mode) */}
      {isStreamer && liveLocal && <PipOverlayControls previewEl={localVideoRef.current} />}

      {/* Connection state chip — only visible while stream is negotiating or has issues */}
      {connState !== "idle" && connState !== "connected" && (
        <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] sc-mono uppercase tracking-widest bg-black/70 border border-white/10" data-testid="stream-conn-state">
          {connState === "connecting"   && <><span className="rec-dot" style={{ background: "#F59E0B", boxShadow: "0 0 6px #F59E0B" }} /> Connecting</>}
          {connState === "reconnecting" && <><span className="rec-dot" style={{ background: "#F59E0B", boxShadow: "0 0 6px #F59E0B" }} /> Reconnecting</>}
          {connState === "failed"       && <><span className="rec-dot" style={{ background: "#FF3366", boxShadow: "0 0 6px #FF3366" }} /> Connection failed</>}
        </div>
      )}
      {connState === "connected" && (
        <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] sc-mono uppercase tracking-widest bg-black/60 border border-emerald-500/40 text-emerald-400" data-testid="stream-conn-state">
          <span className="rec-dot" style={{ background: "#10B981", boxShadow: "0 0 6px #10B981" }} /> Connected
        </div>
      )}

      {!showVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#18181B] to-black">
          <img src="https://images.unsplash.com/photo-1534423861386-85a16f5d13fd" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30"  loading="lazy" decoding="async" />
          <div className="relative z-10 text-center px-6">
            {channel?.is_live ? (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF3366] rounded-sm mb-3">
                  <span className="live-dot" style={{ background: "white" }} /> <span className="text-xs font-bold tracking-widest">LIVE NOW</span>
                </div>
                <div className="sc-heading text-2xl font-bold">{channel?.title}</div>
                <div className="text-zinc-400 text-sm mt-1">{channel?.username} • {channel?.category}</div>
                <div className="text-xs text-zinc-500 mt-3">Connecting to stream...</div>
              </>
            ) : (
              <>
                <Radio className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
                <div className="sc-heading text-2xl font-bold text-zinc-300">{isStreamer ? "You're offline" : "Streamer is offline"}</div>
                <div className="text-zinc-500 text-sm mt-1">{isStreamer ? "Hit Go Live to start broadcasting" : "Check back soon."}</div>
              </>
            )}
          </div>
        </div>
      )}

      {isStreamer && (
        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          {!liveLocal ? (
            <>
              <button
                data-testid={TIDS.channelGoLive}
                onClick={openPicker}
                disabled={startingMedia}
                className="sc-btn flex items-center gap-2 disabled:opacity-60"
              >
                {startingMedia ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {startingMedia ? "Starting…" : "Go Live"}
              </button>
              <button
                data-testid="channel-golive-quick"
                onClick={() => startMedia({})}
                disabled={startingMedia}
                className="sc-btn-ghost text-xs disabled:opacity-60"
                title="Skip the device picker and use system defaults"
              >
                Quick start
              </button>
            </>
          ) : (
            <>
              <button data-testid={TIDS.channelOffline} onClick={stopMedia} className="sc-btn-ghost text-xs">End Stream</button>
              <button onClick={toggleCam} className="p-2 bg-black/60 border border-white/10 rounded-sm" title="Toggle Camera">
                {camOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4 text-[#FF3366]" />}
              </button>
              <button onClick={toggleMic} className="p-2 bg-black/60 border border-white/10 rounded-sm" title="Toggle Mic">
                {micOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4 text-[#FF3366]" />}
              </button>
              {screenShare && (
                <div className="px-2 py-1 rounded-sm bg-[color:var(--sc-cyan)]/10 border border-[color:var(--sc-cyan)]/40 text-[10px] sc-mono uppercase tracking-widest text-[color:var(--sc-cyan)]" data-testid="screen-mode-chip">
                  {streaming.pipMode ? "Screen + Camera" : "Screen share"}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Pre-flight Device Picker modal (streamer-only). Renders as a fixed overlay so it doesn't get clipped by the 16:9 video frame. */}
      {isStreamer && pickerOpen && !liveLocal && (
        <div
          className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          data-testid="device-picker-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setPickerOpen(false); }}
        >
          <div className="w-full max-w-md bg-[#0c0c0e] border border-[color:var(--sc-cyan)]/40 rounded-md shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="sc-label text-[color:var(--sc-cyan)] tracking-widest">Go Live</div>
                <div className="sc-heading text-lg font-bold mt-0.5">Choose your inputs</div>
              </div>
              <button
                data-testid="device-picker-close"
                onClick={() => setPickerOpen(false)}
                className="p-1.5 rounded-sm border border-white/10 text-zinc-400 hover:border-white/25 hover:text-white"
                title="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="sc-label block mb-1.5">Camera</label>
                <select
                  data-testid="device-picker-camera"
                  value={selectedCam}
                  onChange={(e) => setSelectedCam(e.target.value)}
                  disabled={devices.cameras.length === 0}
                  className="sc-input w-full text-sm"
                >
                  <option value="">System default</option>
                  {devices.cameras.map((d, i) => (
                    <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="sc-label block mb-1.5">Microphone</label>
                <select
                  data-testid="device-picker-mic"
                  value={selectedMic}
                  onChange={(e) => setSelectedMic(e.target.value)}
                  disabled={devices.mics.length === 0}
                  className="sc-input w-full text-sm"
                >
                  <option value="">System default</option>
                  {devices.mics.map((d, i) => (
                    <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>
                  ))}
                </select>
              </div>
              <div className="text-[11px] text-zinc-500">
                Tip: grant camera & mic permission in your browser so the device names appear. Your selection is only used for this stream.
              </div>
            </div>
            <div className="px-5 py-4 border-t border-white/10 flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  data-testid="device-picker-start-camera"
                  onClick={() => startMedia({ cameraId: selectedCam || undefined, micId: selectedMic || undefined })}
                  disabled={startingMedia}
                  className="sc-btn flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {startingMedia ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  Camera only
                </button>
                <button
                  data-testid="device-picker-start-screen"
                  onClick={() => startMedia({ micId: selectedMic || undefined, screen: true })}
                  disabled={startingMedia}
                  className="sc-btn-ghost flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Monitor className="w-4 h-4" />
                  Screen only
                </button>
              </div>
              <button
                data-testid="device-picker-start-screen-camera"
                onClick={() => startMedia({ cameraId: selectedCam || undefined, micId: selectedMic || undefined, screen: true, withCamera: true })}
                disabled={startingMedia}
                className="sc-btn flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: "var(--sc-cyan)", color: "#000" }}
                title="Share your screen with a picture-in-picture of your camera"
              >
                <Monitor className="w-4 h-4" /><Camera className="w-4 h-4" />
                Screen + Camera (PiP)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
